import React, { useState, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AccountCircle, ExitToApp, Upload } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { standardsAPI } from '../../services/api';

const Header: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
    errors?: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Debug logging
  console.log('Header - isAuthenticated:', isAuthenticated);
  console.log('Header - user:', user);

  const handleImportClick = () => {
    setImportResult(null);
    setImportDialogOpen(true);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      console.log('=== NCEA Import Started ===');
      console.log('File name:', file.name);
      console.log('File size:', file.size, 'bytes', `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);

      // Read file as text and split into chunks to avoid Vercel's 4.5MB limit
      const text = await file.text();
      console.log('File read successfully, text length:', text.length, 'characters');
      
      const lines = text.split('\n');
      console.log('Total lines in file:', lines.length);
      
      const dataLines = lines.slice(1).filter(line => line.trim() !== ''); // Skip header
      console.log('Data lines (excluding header and empty):', dataLines.length);
      
      // Calculate average line length
      const avgLineLength = dataLines.length > 0 
        ? dataLines.reduce((sum, line) => sum + line.length, 0) / dataLines.length 
        : 0;
      console.log('Average line length:', avgLineLength.toFixed(0), 'characters');
      
      const CHUNK_SIZE = 100; // Reduced to 100 lines per chunk
      const chunks: string[][] = [];
      
      for (let i = 0; i < dataLines.length; i += CHUNK_SIZE) {
        chunks.push(dataLines.slice(i, i + CHUNK_SIZE));
      }
      console.log('Created', chunks.length, 'chunks of', CHUNK_SIZE, 'lines each');

      let totalImported = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      const allErrors: string[] = [];

      // Process each chunk via JSON endpoint
      for (let i = 0; i < chunks.length; i++) {
        const chunkData = chunks[i];
        const chunkJson = JSON.stringify({ lines: chunkData });
        console.log(`Chunk ${i + 1}/${chunks.length}: ${chunkData.length} lines, ${chunkJson.length} bytes (${(chunkJson.length / 1024).toFixed(1)} KB)`);
        
        try {
          const response = await standardsAPI.importStandardsJson(chunkData);
          console.log(`Chunk ${i + 1} response:`, response.data);
          totalImported += response.data.imported || 0;
          totalUpdated += response.data.updated || 0;
          totalSkipped += response.data.skipped || 0;
          if (response.data.errors) {
            allErrors.push(...response.data.errors);
          }
        } catch (chunkError: any) {
          console.error(`Chunk ${i + 1} failed:`, chunkError.response?.status, chunkError.response?.data || chunkError.message);
          throw chunkError;
        }
      }

      console.log('=== Import Complete ===');
      console.log('Total imported:', totalImported);
      console.log('Total updated:', totalUpdated);
      console.log('Total skipped:', totalSkipped);

      setImportResult({
        success: true,
        message: `Import complete: ${totalImported} new standards imported, ${totalUpdated} standards updated, ${totalSkipped} skipped (processed ${chunks.length} chunks)`,
        errors: allErrors.slice(0, 10),
      });
    } catch (error: any) {
      console.error('=== Import Failed ===');
      console.error('Error:', error);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      setImportResult({
        success: false,
        message: error.response?.data?.error || 'Failed to import standards',
        errors: error.response?.data?.details ? [error.response.data.details] : undefined,
      });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCloseDialog = () => {
    setImportDialogOpen(false);
    setImportResult(null);
  };

  return (
    <>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Assessment Manager
          </Typography>
          
          {isAuthenticated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                color="inherit"
                startIcon={<Upload />}
                onClick={handleImportClick}
                size="small"
              >
                Import NCEA Standards
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountCircle />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'inherit',
                    fontWeight: 500,
                    minWidth: 'fit-content'
                  }}
                >
                  {user?.name || 'User'}
                </Typography>
              </Box>
              <IconButton
                color="inherit"
                onClick={logout}
                title="Logout"
              >
                <ExitToApp />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Import NCEA Standards Dialog */}
      <Dialog open={importDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import NCEA Standards</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a * (asterisk) delimited file containing NCEA standards.
              The first row will be ignored (header row).
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Required columns:</strong>
              <br />• Column 1: Assessment Code
              <br />• Column 4: Title/Description
              <br />• Column 5: Credits
              <br />• Column 15: I (Internal) or E (External)
            </Typography>
            
            <input
              type="file"
              accept=".txt,.csv"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
              id="ncea-standards-file-input"
            />
            <label htmlFor="ncea-standards-file-input">
              <Button
                variant="contained"
                component="span"
                disabled={importing}
                startIcon={importing ? <CircularProgress size={20} /> : <Upload />}
              >
                {importing ? 'Importing...' : 'Select File'}
              </Button>
            </label>

            {importResult && (
              <Alert 
                severity={importResult.success ? 'success' : 'error'} 
                sx={{ mt: 2 }}
              >
                {importResult.message}
                {importResult.errors && importResult.errors.length > 0 && (
                  <Box sx={{ mt: 1, fontSize: '0.85em' }}>
                    <strong>Errors:</strong>
                    <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                      {importResult.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;
